import { Menu } from "antd";
import type { MenuProps } from "antd";
import logo from "assets/images/logo.png";
import { observer } from "mobx-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import type { Route } from "router";
import { permissionStore } from "store/permissionStore";
import { userStore } from "store/userStore";
import { can } from "utils/auth";
import "../styles/Sidebar.scss";

export const SIDENAV_WIDTH = 245;

const canAccess = (route: Route) => !!route.isPublic || can(route.path || "", "VIEW");

const buildMenuItems = (routes: Route[]): MenuProps["items"] => {
  return routes
    .filter(
      (route) =>
        (route.checkIsDev ? userStore.info.isDev : true) &&
        !route.hidden &&
        canAccess(route),
    )
    .flatMap((route) => {
      const children = route.children?.filter(
        (child) => !child.hidden && child.element !== null && canAccess({ ...child, path: `${route.path}/${child.path}` }),
      );

      if (route.children?.length && !children?.length) {
        return [];
      }

      if (children?.length === 1) {
        const child = children[0];
        return [
          {
            key: `${route.path}/${child.path}`,
            icon: route.icon,
            label: route.title,
          },
        ];
      }

      if (children?.length) {
        return [
          {
            key: route.path!,
            icon: route.icon,
            label: route.title,
            children: children.map((item) => ({
              key: `${route.path}/${item.path}`,
              icon: item.icon,
              label: item.title,
            })),
          },
        ];
      }

      return [
        {
          key: route.path!,
          icon: route.icon,
          label: route.title,
        },
      ];
    });
};

const useAdminMenuItems = () => {
  return useMemo(
    () => buildMenuItems(permissionStore.accessRoutes),
    [permissionStore.accessRoutes, userStore.info.isDev, userStore.info.role?.permissions],
  );
};

const resolveMenuKey = (pathname: string) => {
  if (/^\/quote\/\d+$/.test(pathname)) {
    return "/quote";
  }
  if (pathname === "/opportunity/create" || pathname.startsWith("/opportunity/")) {
    return "/opportunity";
  }
  return pathname;
};

type SidebarMenuProps = {
  onNavigate?: () => void;
};

export const SidebarMenu = observer(({ onNavigate }: SidebarMenuProps) => {
  const location = useLocation();
  const [openKeys, setOpenKeys] = useState<string[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const navigate = useNavigate();
  const menuItems = useAdminMenuItems();

  useEffect(() => {
    const routes = permissionStore.accessRoutes;
    const parentRoute = routes.find(
      (route) => route.children && route.path && location.pathname.startsWith(route.path),
    );

    if (parentRoute?.path) {
      setOpenKeys([parentRoute.path]);
    }

    setIsLoaded(true);
  }, [location.pathname]);

  const handleOpenChange: MenuProps["onOpenChange"] = (keys) => {
    setOpenKeys(keys as string[]);
  };

  const handleMenuClick: MenuProps["onClick"] = ({ key }) => {
    navigate(key);
    onNavigate?.();
  };

  return (
    <div className="admin-sidebar__inner">
      <div className="logo-box sticky top-0 flex items-center justify-center px-6 py-3 backdrop-blur-xs">
        <Link to="/" onClick={onNavigate}>
          <img src={logo} className="logo-lg" alt="Logo" />
        </Link>
      </div>

      {isLoaded && (
        <Menu
          className="sidebar"
          theme="light"
          mode="inline"
          selectedKeys={[resolveMenuKey(location.pathname)]}
          openKeys={openKeys}
          onOpenChange={handleOpenChange}
          onClick={handleMenuClick}
          items={menuItems}
        />
      )}
    </div>
  );
});

export const TopNavMenu = observer(() => {
  const location = useLocation();
  const navigate = useNavigate();
  const menuItems = useAdminMenuItems();

  const handleMenuClick = useCallback<NonNullable<MenuProps["onClick"]>>(
    ({ key }) => {
      navigate(key);
    },
    [navigate],
  );

  return (
    <Menu
      className="admin-top-menu"
      mode="horizontal"
      disabledOverflow
      theme="light"
      selectedKeys={[resolveMenuKey(location.pathname)]}
      onClick={handleMenuClick}
      items={menuItems}
    />
  );
});
